/**
 * Runtime Config Helper
 *
 * Thin wrapper around process.env so API clients can import getConfig()
 * without every call site having to spell out `process.env[KEY] || ''`.
 *
 * All values are provided at build time via .env (local) or Vercel
 * Environment Variables (production). REACT_APP_* keys are inlined
 * into the client bundle by react-scripts.
 *
 * IMPORTANT: Create React App / react-scripts only inlines
 * `process.env.SOME_LITERAL_NAME` — it does NOT inline dynamic lookups
 * like `process.env[key]`. So we build a literal-keyed map here and do
 * a runtime map lookup in `getConfig`. Every REACT_APP_* key referenced
 * through `getConfig` must be listed below.
 */

const CONFIG: Record<string, string | undefined> = {
  // Supabase
  REACT_APP_SUPABASE_URL: process.env.REACT_APP_SUPABASE_URL,
  REACT_APP_SUPABASE_ANON_KEY: process.env.REACT_APP_SUPABASE_ANON_KEY,

  // Google (OAuth / Drive / CSE)
  REACT_APP_GOOGLE_CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID,
  REACT_APP_GOOGLE_DRIVE_CLIENT_ID: process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID,
  REACT_APP_GOOGLE_DRIVE_API_KEY: process.env.REACT_APP_GOOGLE_DRIVE_API_KEY,
  REACT_APP_GOOGLE_DRIVE_APP_ID: process.env.REACT_APP_GOOGLE_DRIVE_APP_ID,
  REACT_APP_GOOGLE_CSE_API_KEY: process.env.REACT_APP_GOOGLE_CSE_API_KEY,
  REACT_APP_GOOGLE_CSE_ID: process.env.REACT_APP_GOOGLE_CSE_ID,

  // Meta / Facebook / Instagram
  REACT_APP_META_AD_ACCOUNT_ID: process.env.REACT_APP_META_AD_ACCOUNT_ID,
  REACT_APP_META_ACCESS_TOKEN: process.env.REACT_APP_META_ACCESS_TOKEN,
  REACT_APP_META_FACEBOOK_PAGE_ID: process.env.REACT_APP_META_FACEBOOK_PAGE_ID,
  REACT_APP_META_INSTAGRAM_ACCOUNT_ID: process.env.REACT_APP_META_INSTAGRAM_ACCOUNT_ID,

  // X / Twitter
  REACT_APP_X_USERNAME: process.env.REACT_APP_X_USERNAME,
  REACT_APP_X_API_KEY: process.env.REACT_APP_X_API_KEY,
  REACT_APP_X_API_SECRET: process.env.REACT_APP_X_API_SECRET,
  REACT_APP_X_ACCESS_TOKEN: process.env.REACT_APP_X_ACCESS_TOKEN,
  REACT_APP_X_ACCESS_TOKEN_SECRET: process.env.REACT_APP_X_ACCESS_TOKEN_SECRET,

  // Auth0
  REACT_APP_AUTH0_BASE_URL: process.env.REACT_APP_AUTH0_BASE_URL,
  REACT_APP_AUTH0_CLIENT_ID: process.env.REACT_APP_AUTH0_CLIENT_ID,
  REACT_APP_AUTH0_ISSUER_BASE_URL: process.env.REACT_APP_AUTH0_ISSUER_BASE_URL,

  // OpenAI
  REACT_APP_OPENAI_API_KEY: process.env.REACT_APP_OPENAI_API_KEY,

  // QuickBooks
  REACT_APP_QUICKBOOKS_REDIRECT_URI: process.env.REACT_APP_QUICKBOOKS_REDIRECT_URI,
  REACT_APP_QUICKBOOKS_ENVIRONMENT: process.env.REACT_APP_QUICKBOOKS_ENVIRONMENT,

  // Make.com
  REACT_APP_MAKE_TEAM_ID: process.env.REACT_APP_MAKE_TEAM_ID,
  REACT_APP_MAKE_ORG_ID: process.env.REACT_APP_MAKE_ORG_ID,

  // Buffer
  REACT_APP_BUFFER_ACCESS_TOKEN: process.env.REACT_APP_BUFFER_ACCESS_TOKEN,

  // Dropbox
  REACT_APP_DROPBOX_APP_KEY: process.env.REACT_APP_DROPBOX_APP_KEY,

  // Mapbox / GTM
  REACT_APP_MAPBOX_API_KEY: process.env.REACT_APP_MAPBOX_API_KEY,
  REACT_APP_GTM_CONTAINER_ID: process.env.REACT_APP_GTM_CONTAINER_ID,
};

/**
 * Get a config value from the inlined env map.
 * Returns '' when unset so call sites can treat config as a plain string.
 */
export function getConfig(key: string): string {
  return CONFIG[key] || '';
}
